import "firebase/analytics";
import {
  type Auth,
  type User,
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  type Database,
  child,
  set as firebaseSet,
  get as getDataSnapshot,
  push,
  ref,
} from "firebase/database";
import create from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import firebaseFileUpload from "@/utils/firebaseFileUpload";
import {
  getFirebaseApp,
  getFirebaseDatabase,
} from "@/utils/firebaseInitialize";
import type * as AppType from "@/utils/types";
import { useAppStore } from "./app";

const LOG_TAG = "🔥 Firebase";

//const firebaseApp = getFirebaseApp();
//const firebaseDatabase = getFirebaseDatabase();
//console.debug(LOG_TAG, "firebaseDatabase", { firebaseApp, firebaseDatabase });

export type FirebaseContextProps = {
  db: Database;
  firebaseAuth: Auth;
  signOut: () => Promise<void>;

  songAddGetKey: () => string;
  setListAddGetKey: () => string;

  user: User | null;
  setUser: (T: User | null) => void;
  userModify: (T: AppType.User) => void;
  userProfileGet: (T: AppType.UserId) => Promise<AppType.User>;

  fileUpload: (file: File, userId: AppType.UserId) => Promise<string>;
};

const useFirebaseStore = create<FirebaseContextProps>(
  // @ts-ignore
  subscribeWithSelector((set, get) => ({
    db: getFirebaseDatabase(),
    user: (typeof window !== "undefined"
      ? getAuth(getFirebaseApp())
      : ({} as Auth)
    ).currentUser,
    firebaseAuth:
      typeof window !== "undefined" ? getAuth(getFirebaseApp()) : ({} as Auth),
    signOut: () => {
      console.debug(LOG_TAG, "signOut");
      return signOut(get().firebaseAuth);
    },
    songAddGetKey: () => {
      const { user, db } = get();
      if (!(user && db)) {
        throw new Error(`${LOG_TAG} can't get a songs key without user and db`);
      }
      return push(child(ref(db), `user-songs/${user.uid}`)).key || "";
    },
    setListAddGetKey: () => {
      const { user, db } = get();
      if (!(user && db)) {
        throw new Error(
          `${LOG_TAG} can't get a set list key without user and db`,
        );
      }
      return push(child(ref(db), `user-set-lists/${user.uid}`)).key || "";
    },
    userModify: (user: AppType.User) => {
      const { db } = get();
      firebaseSet(ref(db, `user-profile/${user.id}`), user);
    },
    userProfileGet: async (userId: AppType.UserId) => {
      const { db } = get();
      const snapshot = await getDataSnapshot(ref(db, `user-profile/${userId}`));
      const user = snapshot.val();
      console.debug(`${LOG_TAG} userProfileGet`, { userId, user });
      return user;
    },
    fileUpload: async (file: File, userId: AppType.UserId): Promise<string> => {
      return firebaseFileUpload(getFirebaseApp(), file, userId);
    },
    setUser: (user) => {
      set({ user });
    },
  })),
);

if (typeof window !== "undefined") {
  const { firebaseAuth, setUser } = useFirebaseStore.getState();
  onAuthStateChanged(firebaseAuth, (user: User | null) => {
    console.debug(LOG_TAG, "user onAuthStateChanged", { user });
    useAppStore.setState({ waitingOnAuth: false });
    setUser(user);
  });
}

export default useFirebaseStore;
